import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedService } from '@services/shared.service';
import { PagesService } from '@services/pages.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

export interface ActivityLogModel {
  id: number;
  created_at: number;
  category: string;
  action: string;
  severity: string;
  description: string;
  user_id: number;
  user_name: string;
  user_email: string;
  user_role: string;
  content_type: string;
  content_id: number;
  content_title: string;
  target_user_id: number;
  changes: any;
}

@Component({
  selector: 'app-activity-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzToolTipModule,
    NzDrawerModule,
    NzSelectModule,
    NzDatePickerModule,
    NzModalModule,
    NzButtonModule,
    TranslateModule,
  ],
  templateUrl: './activity-logs.component.html',
  styleUrls: ['./activity-logs.component.scss']
})
export class ActivityLogsComponent implements OnInit, OnDestroy {
  private sharedService = inject(SharedService);
  private pagesService = inject(PagesService);
  private secureStorage = inject(SecureStorageService);
  private translateService = inject(TranslateService);

  logs: ActivityLogModel[] = [];
  totalLogs: number = 0;
  searchTerm: string = '';

  // Pagination
  currentPage: number = 1;
  perPage: number = 20;

  // Sorting
  sortBy: string = '';
  sortOrder: string = '';

  // Filters
  selectedCategory: string = '';
  selectedAction: string = '';
  selectedSeverity: string = '';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  selectedContentType: string = '';

  // Filter Modal State
  filterModalVisible: boolean = false;
  tempCategory: string = '';
  tempAction: string = '';
  tempSeverity: string = '';
  tempDateFrom: Date | null = null;
  tempDateTo: Date | null = null;

  // Company ID from logged-in user
  companyId: string = '';

  // Detail drawer
  drawerVisible: boolean = false;
  selectedLog: ActivityLogModel | null = null;
  changesList: { field: string; old_value: any; new_value: any }[] = [];

  // Filter options (label holds an i18n key, translated in the template)
  categoryOptions = [
    { value: 'user_activity', label: 'activity-logs.categories.user_activity' },
    { value: 'contest', label: 'activity-logs.categories.contest' },
    { value: 'subscription', label: 'activity-logs.categories.subscription' },
    { value: 'system', label: 'activity-logs.categories.system' },
  ];

  actionOptions = [
    { value: 'login', label: 'activity-logs.actions.login' },
    { value: 'logout', label: 'activity-logs.actions.logout' },
    { value: 'create', label: 'activity-logs.actions.create' },
    { value: 'update', label: 'activity-logs.actions.update' },
    { value: 'delete', label: 'activity-logs.actions.delete' },
    { value: 'approve', label: 'activity-logs.actions.approve' },
    { value: 'reject', label: 'activity-logs.actions.reject' },
  ];

  severityOptions = [
    { value: 'info', label: 'activity-logs.severities.info' },
    { value: 'warning', label: 'activity-logs.severities.warning' },
    { value: 'error', label: 'activity-logs.severities.error' },
    { value: 'critical', label: 'activity-logs.severities.critical' },
  ];

  // Debounced search
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadLogs(1, false);
    });

    this.initCompanyId();
    this.loadLogs(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  private initCompanyId(): void {
    try {
      const userDetails = this.secureStorage.getItem<any>('userDetails') || {};
      // The old backend sent isCompany as an array; this one sends the company itself. Reading
      // only the array shape left the id empty, and the page then declined to ask for anything.
      const company = Array.isArray(userDetails.isCompany)
        ? userDetails.isCompany[0]
        : userDetails.isCompany;
      this.companyId = company?.id ? String(company.id) : '';
    } catch {
      this.companyId = '';
    }
  }

  loadLogs(page: number = 1, showLoader: boolean = true): void {
    // No guard on companyId: the server decides whose logs these are from the token, so a page
    // that will not even ask because local storage looked unfamiliar is a screen breaking
    // itself over something the server never reads.
    if (showLoader) this.sharedService.showLoader();

    const params: any = {
      page,
      per_page: this.perPage,
      search: this.searchTerm?.trim() || '',
      category: this.selectedCategory || '',
      action: this.selectedAction || '',
      severity: this.selectedSeverity || '',
      content_type: this.selectedContentType || '',
      user_id: this.companyId,
    };

    if (this.dateFrom) {
      params.date_from = this.formatDateParam(this.dateFrom);
    }
    if (this.dateTo) {
      params.date_to = this.formatDateParam(this.dateTo);
    }
    if (this.sortBy) {
      params.sort_by = this.sortBy;
      params.sort_order = this.sortOrder || 'desc';
    }

    this.pagesService.getAll('ACTIVITY_LOGS', params).subscribe({
      next: (response: any) => {
        // Handle both response formats
        const data = response?.data || response;
        const logs = data?.logs || data?.items || (Array.isArray(data) ? data : []);
        const pagination = data?.pagination;

        this.logs = logs.map((log: any) => ({
          ...log,
          category: this.normalizeLogCategory(log)
        }));
        this.totalLogs = pagination?.total_records || data?.itemsTotal || logs.length;
        this.currentPage = pagination?.current_page || data?.curPage || page;
        if (showLoader) this.sharedService.hideLoader();
      },
      error: (err: any) => {
        if (showLoader) this.sharedService.hideLoader();
        this.sharedService.createNotification('error', err?.error?.message || 'Failed to load activity logs');
      }
    });
  }

  // Pagination handlers
  onPageIndexChange(index: number): void {
    this.currentPage = index;
    this.loadLogs(index);
  }

  onPageSizeChange(size: number): void {
    this.perPage = size;
    this.currentPage = 1;
    this.loadLogs(1);
  }

  // Sort handler
  onSort(field: string, order: string | null): void {
    if (order) {
      this.sortBy = field;
      this.sortOrder = order === 'ascend' ? 'asc' : 'desc';
    } else {
      this.sortBy = '';
      this.sortOrder = '';
    }
    this.currentPage = 1;
    this.loadLogs(1);
  }

  // Filter handlers
  openFilterModal(): void {
    this.tempCategory = this.selectedCategory;
    this.tempAction = this.selectedAction;
    this.tempSeverity = this.selectedSeverity;
    this.tempDateFrom = this.dateFrom;
    this.tempDateTo = this.dateTo;
    this.filterModalVisible = true;
  }

  closeFilterModal(): void {
    this.filterModalVisible = false;
  }

  applyFiltersFromModal(): void {
    this.selectedCategory = this.tempCategory;
    this.selectedAction = this.tempAction;
    this.selectedSeverity = this.tempSeverity;
    this.dateFrom = this.tempDateFrom;
    this.dateTo = this.tempDateTo;
    this.filterModalVisible = false;
    this.currentPage = 1;
    this.loadLogs(1);
  }

  resetFiltersInModal(): void {
    this.tempCategory = '';
    this.tempAction = '';
    this.tempSeverity = '';
    this.tempDateFrom = null;
    this.tempDateTo = null;
    this.selectedCategory = '';
    this.selectedAction = '';
    this.selectedSeverity = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.filterModalVisible = false;
    this.currentPage = 1;
    this.loadLogs(1);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadLogs(1);
  }

  clearFilters(): void {
    this.selectedCategory = '';
    this.selectedAction = '';
    this.selectedSeverity = '';
    this.selectedContentType = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadLogs(1);
  }

  hasActiveFilters(): boolean {
    return !!(this.selectedCategory || this.selectedAction || this.selectedSeverity ||
      this.selectedContentType || this.dateFrom || this.dateTo);
  }

  // Detail drawer
  openDetailDrawer(log: ActivityLogModel): void {
    this.selectedLog = log;
    this.changesList = this.parseChanges(log.changes);
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedLog = null;
    this.changesList = [];
  }

  private parseChanges(changes: any): { field: string; old_value: any; new_value: any }[] {
    if (!changes) return [];
    let raw: { field: string; old_value: any; new_value: any }[] = [];
    if (Array.isArray(changes)) {
      raw = changes.map((c: any) => ({
        field: c.field || c.field_name || '',
        old_value: c.old_value ?? c.old ?? '',
        new_value: c.new_value ?? c.new ?? '',
      }));
    } else if (typeof changes === 'object') {
      raw = Object.keys(changes).map(key => ({
        field: key,
        old_value: changes[key]?.old_value ?? changes[key]?.old ?? '',
        new_value: changes[key]?.new_value ?? changes[key]?.new ?? '',
      }));
    }
    // Internal ids (foreign keys, UUIDs) mean nothing to the user, so they are left out
    return raw
      .filter(c => c.field && !this.isInternalIdField(c.field) && !(this.isUuid(c.old_value) || this.isUuid(c.new_value)))
      .map(c => ({
        field: this.formatFieldLabel(c.field),
        old_value: this.formatChangeValue(c.old_value),
        new_value: this.formatChangeValue(c.new_value),
      }));
  }

  private isInternalIdField(field: string): boolean {
    const f = field.toLowerCase();
    return f === 'id' || f.endsWith('_id') || f.endsWith('_ids') || f === 'uuid';
  }

  private isUuid(value: any): boolean {
    return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
  }

  // "ai_token_limit" -> "AI Token Limit"
  private formatFieldLabel(field: string): string {
    const acronyms = ['ai', 'ar', 'sdk', 'vip', 'api', 'url', 'sms', 'otp', 'usd', 'qr'];
    return field
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map(word => acronyms.includes(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private formatChangeValue(value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean' || value === 'true' || value === 'false') {
      const isTrue = value === true || value === 'true';
      return this.translateOrFallback(isTrue ? 'activity-logs.yes' : 'activity-logs.no', isTrue ? 'Yes' : 'No');
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) return this.formatDateTime(date.getTime());
    }
    if (typeof value === 'string' && /^\s*\{[\s\S]*\}\s*$/.test(value)) {
      try { value = JSON.parse(value); } catch { /* not JSON, show as text */ }
    }
    if (typeof value === 'object') {
      // Nested objects (e.g. payment metadata) become a readable "Label: value" list
      return Object.keys(value)
        .filter(key => !this.isInternalIdField(key) && !this.isUuid(value[key]))
        .map(key => `${this.formatFieldLabel(key)}: ${this.formatChangeValue(value[key])}`)
        .join(', ') || '—';
    }
    // Enum codes such as "CLIENT_USER" or "PENDING" -> "Client User" / "Pending"
    if (typeof value === 'string' && /^[A-Z][A-Z0-9_]*$/.test(value)) {
      return this.formatFieldLabel(value.toLowerCase());
    }
    return String(value);
  }

  getRoleLabel(role: string): string {
    if (!role) return '-';
    const norm = role.toLowerCase();
    return this.translateOrFallback(`activity-logs.roles.${norm}`, this.formatFieldLabel(norm));
  }

  // Content type usually repeats the category, so only show it when it adds information
  showContentType(log: ActivityLogModel): boolean {
    if (!log?.content_type) return false;
    return log.content_type.toLowerCase() !== (log.category || '').toLowerCase();
  }

  // CSV Export
  exportCsv(): void {
    if (this.totalLogs === 0) {
      this.sharedService.createNotification('error', 'No logs to export');
      return;
    }

    this.sharedService.showLoader();

    const params: any = {
      page: 1,
      per_page: this.totalLogs,
      search: this.searchTerm?.trim() || '',
      category: this.selectedCategory || '',
      action: this.selectedAction || '',
      severity: this.selectedSeverity || '',
      content_type: this.selectedContentType || '',
      user_id: this.companyId,
    };

    if (this.dateFrom) params.date_from = this.formatDateParam(this.dateFrom);
    if (this.dateTo) params.date_to = this.formatDateParam(this.dateTo);
    if (this.sortBy) {
      params.sort_by = this.sortBy;
      params.sort_order = this.sortOrder || 'desc';
    }

    this.pagesService.getAll('ACTIVITY_LOGS', params).subscribe({
      next: (response: any) => {
        const data = response?.data || response;
        const allLogs = data?.logs || data?.items || (Array.isArray(data) ? data : []);
        const normalizedLogs = allLogs.map((log: any) => ({
          ...log,
          category: this.normalizeLogCategory(log)
        }));
        this.generateCsv(normalizedLogs);
        this.sharedService.hideLoader();
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', err?.error?.message || 'Failed to fetch data for export');
      }
    });
  }

  private generateCsv(logs: ActivityLogModel[]): void {
    const isArabic = localStorage.getItem('lang') === 'ar';
    const headers = isArabic
      ? [
          this.translateService.instant('activity-logs.date-time'),
          this.translateService.instant('activity-logs.category'),
          this.translateService.instant('activity-logs.action'),
          this.translateService.instant('activity-logs.severity'),
          this.translateService.instant('activity-logs.description'),
          this.translateService.instant('activity-logs.content'),
        ]
      : ['Date & Time', 'Category', 'Action', 'Severity', 'Description', 'Content'];
    const rows = logs.map(log => [
      this.formatDateTime(log.created_at),
      this.getCategoryLabel(log.category),
      this.getActionLabel(log.action),
      this.getSeverityLabel(log.severity),
      `"${(log.description || '').replace(/"/g, '""')}"`,
      log.content_title || '',
    ]);

    const bom = '\uFEFF';
    const csvContent = bom + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `activity_logs_${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    this.sharedService.createNotification('success', 'CSV exported successfully');
  }

  // Utility methods
  formatDateTime(timestamp: number): string {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  private formatDateParam(date: Date): string {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  private normalizeLogCategory(log: any): string {
    if (!log) return 'user_activity';
    const desc = String(log.description || '').toLowerCase();
    const contentType = String(log.content_type || '').toLowerCase();
    const rawCategory = String(log.category || '').toLowerCase();

    // Force sub-user or user management logs to 'user_activity' category
    if (
      desc.includes('sub user') ||
      desc.includes('sub-user') ||
      desc.includes('subuser') ||
      desc.includes('user created') ||
      desc.includes('user updated') ||
      desc.includes('user removed') ||
      desc.includes('user deleted') ||
      contentType.includes('sub_user') ||
      contentType.includes('subuser') ||
      contentType === 'user' ||
      contentType === 'sub_user'
    ) {
      return 'user_activity';
    }

    // Engagement logs are campaign activity; show them under "Campaign".
    if (['contest', 'contests', 'engagement', 'engagements'].includes(rawCategory)) return 'contest';
    if (rawCategory === 'subscription' || rawCategory === 'subscriptions') return 'subscription';
    if (rawCategory === 'system') return 'system';
    if (rawCategory === 'user' || rawCategory === 'user_activity' || rawCategory === 'user_management') return 'user_activity';

    return rawCategory || 'user_activity';
  }

  getCategoryClass(category: string): string {
    const norm = (category || '').toLowerCase();
    const map: Record<string, string> = {
      user_activity: 'badge-blue',
      user: 'badge-blue',
      user_management: 'badge-blue',
      subuser: 'badge-blue',
      sub_user: 'badge-blue',
      contest: 'badge-green',
      subscription: 'badge-yellow',
      system: 'badge-red',
    };
    return map[norm] || 'badge-default';
  }

  getSeverityClass(severity: string): string {
    const map: Record<string, string> = {
      info: 'severity-info',
      warning: 'severity-warning',
      error: 'severity-error',
      critical: 'severity-critical',
    };
    return map[severity] || 'severity-info';
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key);
    return translated === key ? fallback : translated;
  }

  getCategoryLabel(category: string): string {
    if (!category) return '-';
    const norm = category.toLowerCase();
    if (norm === 'user' || norm === 'user_activity' || norm === 'user_management' || norm === 'subuser' || norm === 'sub_user') {
      return this.translateOrFallback('activity-logs.categories.user_activity', 'User Activity');
    }
    return this.translateOrFallback(`activity-logs.categories.${norm}`, norm.replace(/_/g, ' '));
  }

  getActionLabel(action: string): string {
    if (!action) return '-';
    return this.translateOrFallback(`activity-logs.actions.${action.toLowerCase()}`, action);
  }

  getSeverityLabel(severity: string): string {
    if (!severity) return '-';
    return this.translateOrFallback(`activity-logs.severities.${severity.toLowerCase()}`, severity);
  }

  getPageRangeText(): string {
    const start = this.totalLogs === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    const end = Math.min(this.currentPage * this.perPage, this.totalLogs);
    return `${start}-${end} of ${this.totalLogs} items`;
  }
}
